/**
 * Campo, botão e banner — os três componentes que toda tela de formulário usa
 * (spec 04-TELAS/0.3).
 *
 * Transcritos do protótipo, classe por classe: a baseline visual compara pixel, e
 * um `rounded-lg` no lugar de `rounded-[10px]` reprova.
 *
 * O que eles carregam de regra, e não só de aparência:
 *  - erro de campo é INLINE, com `aria-invalid` e foco — não um alerta genérico;
 *  - digitar no campo limpa o erro dele (tela 1.1);
 *  - botão em carregamento ignora clique repetido — uma requisição por submissão.
 */
import { Children, forwardRef, isValidElement, useCallback, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Icone } from '../shell/icone.tsx'
import { CampoDeData } from './campo-de-data.tsx'
import { DicaContextual } from './dica-contextual.tsx'
import { useConsultaDeTela } from './indice-lateral.tsx'
import { foraDoConjunto, PainelFlutuante, usePosicaoDoPainel } from './painel-flutuante.tsx'
import { useRodapeFixo } from './toast.tsx'
import { mascararCepBr, mascararTelefoneBr } from '../entradas-estruturadas.ts'

/** 15rem — o teto que `.dv-select-list` e `.dv-multi-list` já declaravam em `estilo.css`. */
export const ALTURA_DA_LISTA = 240

/**
 * `aria-describedby` recebe a microcopy E o erro, nesta ordem (lote U1 · A11Y-05).
 *
 * O campo tinha o erro ligado e a microcopy solta: "mínimo de 8 caracteres" estava
 * escrito embaixo do campo e não pertencia a campo nenhum aos olhos do leitor de tela.
 * Quem ouve a tela recebia "Senha, edição" e nada mais — a instrução que evita o erro
 * só aparecia depois de errar.
 */
export function descritoPor(...ids: readonly (string | undefined)[]): string | undefined {
  const presentes = ids.filter((id) => id !== undefined)
  return presentes.length === 0 ? undefined : presentes.join(' ')
}

/**
 * Leva o foco ao primeiro campo inválido quando a validação reprova.
 *
 * Formulário que reprova e não move o foco deixa o usuário de teclado parado no botão
 * "Salvar", com a mensagem de erro numa parte da página que ele não vê e o leitor de
 * tela não anuncia. A ordem do DOM é a ordem visual, então "o primeiro `aria-invalid`"
 * é o primeiro campo com problema — e é para ele que o foco vai.
 */
export function useFocoNoPrimeiroInvalido(erros: object, dentroDe?: RefObject<HTMLElement | null>): void {
  // A dependência é a ASSINATURA dos erros, não o objeto: um `setErros` que devolve o
  // mesmo conjunto (dois submits com o mesmo problema) não deve roubar o foco de novo
  // se o usuário já o tiver movido; um conjunto diferente deve.
  const assinatura = Object.entries(erros)
    .filter(([, mensagem]) => typeof mensagem === 'string' && mensagem !== '')
    .map(([campo]) => campo)
    .sort()
    .join('|')

  useEffect(() => {
    if (assinatura === '') return
    // Sem raiz, o documento: telas com vários formulários alternativos (a 1.1 tem
    // cinco modos) renderizam um de cada vez, e o primeiro inválido do documento é o
    // primeiro inválido do formulário que está na tela.
    const raiz: ParentNode = dentroDe?.current ?? document
    raiz.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  }, [assinatura, dentroDe])
}

/*
  `fld` é a classe DO PROTÓTIPO, transportada para `estilo.css` — altura, borda, raio,
  foco, estado de erro e desabilitado vêm de lá.

  Antes daqui havia uma aproximação escrita à mão em Tailwind, e ela é metade do que o
  fundador chamou de "diferente e pobre": os 126 campos das 49 telas usam `.fld`, e o
  app usava outra coisa parecida. Recriar o que já existe pronto é o erro que esta
  linha desfaz.

  `min-h-[44px]` fica por cima da altura de 2.5rem SÓ no celular, e não é preferência:
  é o alvo de toque do canon, e o `estilo.css` já sobe a fonte para 16px lá para o iOS
  não dar zoom no foco.
*/
const CLASSE_DO_CAMPO = 'fld min-h-[44px] md:min-h-0'

export interface PropsDeCampo extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  readonly rotulo: string
  readonly erro?: string | undefined
  readonly microcopy?: string | undefined
  /**
   * Explicação complementar do campo, numa dica ao lado do rótulo (D-203, nível do campo).
   * É a exceção: o padrão é a dica do GRUPO (`Secao`, `LegendaComDica`, `ajuda` do `Modal`). O que a pessoa
   * precisa ver para não errar continua em `microcopy`, visível.
   */
  readonly ajuda?: ReactNode
  /** Conteúdo à direita do rótulo — o "Esqueci a senha" da 1.1 vive aqui. */
  readonly acessorio?: ReactNode
  readonly depoisDoCampo?: ReactNode
  /**
   * Classe extra NO CAMPO — existe por uma tela só, e é medida.
   *
   * A 1.1 escreve `min-h-[44px]` sem redução no `md:`: o cartão de login é touch-first
   * e os três controles medem 44px nos dois viewports. Todo o resto do produto usa os
   * 36px do `.fld`. Sem esta porta, ou o login encolhe ou os outros 85 campos incham.
   *
   * A caixa do `.fld` (altura, padding, borda, raio, fundo, fonte, cor) mora em
   * `@layer components` desde 31/08 (`estilo.css`, "A CAMADA"), e por isso a utilitária de
   * quem chama, que fica em `@layer utilities`, vence a caixa: `pl-12` e `md:h-11` pintam
   * sem `!`. O que continua fora de camada, e vence a utilitária, é o ESTADO (`:focus`,
   * `.err`, `:disabled`). O `md:!h-11` da 1.1 é anterior à camada e continua valendo.
   */
  readonly classe?: string
  /**
   * O símbolo desenhado FORA do campo, à esquerda (item 15 do lote coletado em 24/09/2026 ·
   * D-148 · DS-559): não se seleciona, não se apaga e não recebe clique; o texto do campo
   * guarda só o número. É o prefixo que `telas/3.3-empresa-form.html:241` desenha. Entra
   * no `aria-describedby` (nunca no nome, que continua sendo só o rótulo). Ignorado no
   * campo de data. Quem mostra dinheiro usa o `CampoDeDinheiro`, que o preenche pela moeda.
   */
  readonly prefixo?: string | undefined
}

/**
 * O rótulo do campo, com a dica do campo e o acessório (D-203).
 *
 * Sem dica, a marcação é a de antes: rótulo em bloco ou, com acessório, a linha com o
 * acessório à direita (o `Campo` sempre a escreveu sem `gap`, a `Selecao` com ele). Com dica,
 * o ícone vai AO LADO do `<label>`, nunca dentro: dentro, ele entraria no nome acessível do
 * campo, que o leitor de tela e os testes usam.
 */
export function LinhaDoRotulo({
  rotulo,
  para,
  ajuda,
  idDaDica,
  acessorio,
  espacoComAcessorio = true,
}: {
  rotulo: string
  para: string
  ajuda: ReactNode
  idDaDica: string
  acessorio: ReactNode
  espacoComAcessorio?: boolean
}): React.JSX.Element {
  if (ajuda === undefined || ajuda === null || ajuda === false) {
    const linha = espacoComAcessorio ? 'flex items-center justify-between gap-2 mb-1.5' : 'flex items-center justify-between mb-1.5'
    return (
      <div className={acessorio === undefined ? '' : linha}>
        <label className={'eyebrow text-muted ' + (acessorio === undefined ? 'block mb-1.5' : '')} htmlFor={para}>
          {rotulo}
        </label>
        {acessorio}
      </div>
    )
  }
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        <label className="eyebrow text-muted" htmlFor={para}>
          {rotulo}
        </label>
        <DicaContextual rotulo={rotulo} id={idDaDica}>
          {ajuda}
        </DicaContextual>
      </div>
      {acessorio}
    </div>
  )
}

/** `pl-9` abre espaço para até dois caracteres (R$, €); `pl-12`, para três (US$, MX$, CLP). */
function recuoDoPrefixo(prefixo: string): string {
  return prefixo.length <= 2 ? 'pl-9' : 'pl-12'
}

export const Campo = forwardRef<HTMLInputElement, PropsDeCampo>(function Campo(
  { rotulo, erro, microcopy, ajuda, acessorio, depoisDoCampo, classe = '', prefixo, id, ...resto },
  ref,
) {
  // O id vem do React e não do rótulo: "Nova senha" e "Confirmar nova senha" na
  // mesma página produziam ids parecidos e um `<label for>` ambíguo.
  const automatico = useId()
  const identificador = id ?? automatico
  const idDoErro = `${identificador}-erro`
  const idDaAjuda = `${identificador}-ajuda`
  const idDaDica = `${identificador}-dica`
  const idDoPrefixo = `${identificador}-prefixo`
  /*
    A microcopy CONTINUA com o erro (D-203). Pela classificação, o texto que fica visível é
    o que a pessoa precisa saber para não errar, e ele não pode sumir justamente quando ela
    erra. Até a D-203 o `Campo` e o `CampoDeArquivo` o escondiam, e o `CampoDeTexto`, a
    `Selecao` e o `SeletorComBusca` o mantinham; agora todos mantêm, antes do erro.
  */
  const ajudaVisivel = microcopy !== undefined
  const descricaoDaDica = ajuda === undefined ? undefined : idDaDica
  const comPrefixo = prefixo !== undefined && prefixo !== '' && resto.type !== 'date'
  return (
    <div>
      <LinhaDoRotulo
        rotulo={rotulo}
        para={identificador}
        ajuda={ajuda}
        idDaDica={idDaDica}
        acessorio={acessorio}
        espacoComAcessorio={false}
      />
      <div className={depoisDoCampo === undefined && !comPrefixo ? '' : 'relative'}>
        {/*
          Campo de data é o MESMO campo, com o calendário do protótipo por cima.

          A troca acontece aqui, e não em cada chamada, porque `type="date"` no `Campo` são
          36 ocorrências em 18 arquivos: trocar uma a uma seria 36 chances de esquecer uma e
          36 chances de divergir. `CampoDeData` recebe as mesmas propriedades e o mesmo
          `ref` — por baixo continua o `<input type="date">`, então `id`, `aria-invalid`,
          `aria-describedby`, `min`, `max` e `required` seguem sendo dele.
        */}
        {resto.type === 'date' ? (
          <CampoDeData
            {...resto}
            ref={ref}
            id={identificador}
            aria-invalid={erro === undefined ? undefined : true}
            aria-describedby={descritoPor(ajudaVisivel ? idDaAjuda : undefined, descricaoDaDica, erro === undefined ? undefined : idDoErro)}
            className={`${CLASSE_DO_CAMPO} ${classe} ${erro === undefined ? '' : 'err'} ${depoisDoCampo === undefined ? '' : 'pr-12 md:pr-10'}`}
          />
        ) : (
          <>
            {comPrefixo && (
              <span
                id={idDoPrefixo}
                data-teste="prefixo-do-campo"
                className="pointer-events-none select-none absolute left-3 top-1/2 -translate-y-1/2 text-faint text-[12px] tnum"
              >
                {prefixo}
              </span>
            )}
            <input
              {...resto}
              ref={ref}
              id={identificador}
              aria-invalid={erro === undefined ? undefined : true}
              aria-describedby={descritoPor(comPrefixo ? idDoPrefixo : undefined, ajudaVisivel ? idDaAjuda : undefined, descricaoDaDica, erro === undefined ? undefined : idDoErro)}
              className={`${CLASSE_DO_CAMPO} ${comPrefixo ? recuoDoPrefixo(prefixo) : ''} ${classe} ${erro === undefined ? '' : 'err'} ${depoisDoCampo === undefined ? '' : 'pr-12 md:pr-10'}`}
            />
          </>
        )}
        {depoisDoCampo}
      </div>
      {ajudaVisivel && (
        <p id={idDaAjuda} className="text-[12px] text-muted mt-1">
          {microcopy}
        </p>
      )}
      {erro !== undefined && (
        <p id={idDoErro} className="text-[12px] text-red-600 mt-1 flex items-center gap-1">
          <Icone nome="circle-alert" className="w-3.5 h-3.5" /> <span>{erro}</span>
        </p>
      )}
    </div>
  )
})

/** A palavra que o banco guarda quando a inscrição é isenta. Um lugar só. */
export const SENTINELA_DE_ISENTO = 'Isento'

/**
 * Campo de inscrição com a opção "Isento" — PÓS-V1-001.
 *
 * ## Por que compartilhado
 *
 * Existia como função local do formulário de empresa, usada nas duas inscrições da
 * IDENTIFICAÇÃO. As outras duas ocorrências do par IE/IM no produto — o perfil de
 * faturamento e a entidade DV em Configurações — usavam `Campo` cru, sem a opção. Era o
 * relato: "já existe em parte dos campos".
 *
 * ## O dado, que não muda
 *
 * Não há coluna booleana. A isenção é a sentinela `'Isento'` gravada na própria coluna de
 * texto, e os três estados já se distinguem: `NULL` = não informado, `'Isento'` = isento,
 * qualquer outra coisa = o número. Nenhuma migration, e vazio NUNCA vira isento.
 *
 * ## O que muda em relação à versão local
 *
 * O valor guardado para a restauração nunca é a própria sentinela. Antes, reabrir uma
 * empresa salva como isenta inicializava o `useRef` com a string `'Isento'` — e desmarcar
 * escrevia a PALAVRA "Isento" dentro do campo de número, como se fosse um. Agora
 * desmarcar um isento já salvo deixa o campo vazio e pronto para receber o número.
 *
 * A restauração continua valendo para quem marca e desmarca na MESMA sessão de edição: é
 * memória do componente montado, e é só isso que ela pode ser — ao salvar como isento, o
 * número deixa de existir no banco. Prometer recuperá-lo depois exigiria uma segunda
 * coluna para um dado que a pessoa declarou não ter.
 */
export function CampoComIsento({
  rotulo,
  valor,
  isento,
  aoMudarValor,
  aoMudarIsento,
  teste,
  placeholder,
}: {
  rotulo: string
  valor: string
  isento: boolean
  aoMudarValor: (v: string) => void
  aoMudarIsento: (v: boolean) => void
  teste: string
  /** O exemplo que aparece no campo vazio. Obrigatório: inscrição sem exemplo é campo em que cada pessoa escolhe um formato. */
  placeholder: string
}): React.JSX.Element {
  const guardado = useRef(valor === SENTINELA_DE_ISENTO ? '' : valor)
  return (
    <div>
      <Campo
        rotulo={rotulo}
        value={isento ? SENTINELA_DE_ISENTO : valor}
        disabled={isento}
        onChange={(ev) => { aoMudarValor(ev.target.value) }}
        placeholder={placeholder}
        data-teste={teste}
      />
      <label className="mt-1 flex items-center gap-2 text-[12px] text-muted">
        <input
          type="checkbox"
          checked={isento}
          data-teste={`${teste}-isento`}
          onChange={(ev) => {
            if (ev.target.checked) {
              guardado.current = valor === SENTINELA_DE_ISENTO ? '' : valor
              aoMudarIsento(true)
            } else {
              aoMudarIsento(false)
              aoMudarValor(guardado.current)
            }
          }}
          className="h-3.5 w-3.5 accent-navy"
        />
        Isento
      </label>
    </div>
  )
}

/**
 * Seleção de arquivo no padrão do sistema — PÓS-V1-026.
 *
 * ## O defeito, e por que ele era aritmético
 *
 * Quatro modais passavam `type="file"` a um campo de TEXTO. `Campo` aplica `.fld`, que é
 * `height: 2.25rem; padding: 0 .75rem` — 36px de altura com padding vertical ZERO — a um
 * controle nativo que desenha um botão do sistema operacional mais o nome do arquivo. Não
 * cabe e não alinha: é o "visualmente quebrado" do relato.
 *
 * Dois dos quatro já tinham tentado remendar com `py-2`, e isso PIORA: `.fld` fixa
 * `height`, e com `box-sizing: border-box` o padding come o espaço interno em vez de
 * aumentar a caixa.
 *
 * ## A solução já existia no repositório, em quatro lugares
 *
 * `anexos.tsx`, `comentarios.tsx`, `conciliacao.tsx` e `perfil.tsx` fazem a mesma coisa
 * certa: o `<input type="file">` fica escondido e quem aparece é um botão do sistema.
 * Este componente só encapsula esse padrão — nenhuma biblioteca nova, nenhum fluxo
 * reescrito.
 *
 * ## Duas decisões de acessibilidade que não são detalhe
 *
 * **`sr-only`, não `hidden`.** Um `display:none` sai da ordem de tabulação e o `<label>`
 * deixa de ter o que rotular. Com `sr-only` o input continua focalizável e o leitor de
 * tela continua anunciando o campo pelo rótulo.
 *
 * **Sem `required` no input escondido.** O navegador tenta focar o controle inválido para
 * mostrar o balão; num elemento visualmente oculto ele falha com *"An invalid form control
 * is not focusable"* e o formulário simplesmente não submete, sem dizer por quê. A
 * obrigatoriedade continua existindo — conferida no `salvar` de cada modal, que já
 * mostrava a mensagem inline —, e o rótulo segue marcado com `*` para quem lê a tela.
 */
export function CampoDeArquivo({
  rotulo,
  arquivo,
  aoEscolher,
  accept,
  obrigatorio = false,
  microcopy,
  ajuda,
  erro,
  teste,
}: {
  rotulo: string
  arquivo: File | null
  aoEscolher: (arquivo: File | null) => void
  accept?: string
  /** Só marca o rótulo e o `aria-required`: a conferência é de quem submete (ver acima). */
  obrigatorio?: boolean
  microcopy?: string
  /** Dica do campo (D-203); ver o `Campo`. */
  ajuda?: ReactNode
  erro?: string | undefined
  teste?: string
}): React.JSX.Element {
  const identificador = useId()
  const campo = useRef<HTMLInputElement>(null)
  const idDoErro = `${identificador}-erro`
  const idDaAjuda = `${identificador}-ajuda`
  const idDaDica = `${identificador}-dica`
  // Visível também com erro, como no `Campo` (D-203).
  const ajudaVisivel = microcopy !== undefined
  return (
    <div>
      <LinhaDoRotulo
        rotulo={`${rotulo}${obrigatorio ? ' *' : ''}`}
        para={identificador}
        ajuda={ajuda}
        idDaDica={idDaDica}
        acessorio={undefined}
      />
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={campo}
          id={identificador}
          type="file"
          className="sr-only"
          {...(accept === undefined ? {} : { accept })}
          aria-required={obrigatorio ? true : undefined}
          aria-invalid={erro === undefined ? undefined : true}
          aria-describedby={descritoPor(ajudaVisivel ? idDaAjuda : undefined, ajuda === undefined ? undefined : idDaDica, erro === undefined ? undefined : idDoErro)}
          {...(teste === undefined ? {} : { 'data-teste': teste })}
          onChange={(evento) => {
            aoEscolher(evento.target.files?.[0] ?? null)
          }}
        />
        {/*
          `focus-within` no botão: o foco real vai para o input `sr-only`, e sem isto
          quem navega por teclado não veria onde está.
        */}
        <BotaoSecundario
          type="button"
          className="focus-within:ring-2"
          onClick={() => { campo.current?.click() }}
        >
          <Icone nome="upload" className="h-4 w-4" /> Escolher arquivo
        </BotaoSecundario>
        <span
          className={`min-w-0 flex-1 truncate text-[13px] ${arquivo === null ? 'text-faint' : 'text-text2'}`}
          data-teste={teste === undefined ? undefined : `${teste}-nome`}
          title={arquivo?.name}
        >
          {arquivo?.name ?? 'Nenhum arquivo escolhido'}
        </span>
      </div>
      {ajudaVisivel && (
        <p id={idDaAjuda} className="text-[12px] text-muted mt-1">{microcopy}</p>
      )}
      {erro !== undefined && (
        <p id={idDoErro} className="text-[12px] text-red-600 mt-1 flex items-center gap-1">
          <Icone nome="circle-alert" className="w-3.5 h-3.5" /> <span>{erro}</span>
        </p>
      )}
    </div>
  )
}

/**
 * Máscara do telefone BR aplicada ao digitar (D-66 · GLOSSARIO §Formatação).
 *
 * Mora aqui, e não numa rota, porque duas telas a usam (2.3 e a ficha de RH) e cada
 * rota vira um chunk próprio: importá-la de `rotas/perfil.tsx` arrastaria a tela de
 * perfil inteira para dentro do pacote do RH.
 */
export function mascararTelefone(bruto: string): string {
  return mascararTelefoneBr(bruto)
}

/**
 * Máscara do CEP aplicada ao digitar (0.3 §Máscaras de input).
 *
 * O protótipo é explícito sobre o momento: "a máscara é aplicada AO DIGITAR e a
 * validação usa o valor já mascarado". O app tinha só a metade da validação — oito
 * dígitos conferidos no clique de Buscar, com o hífen por conta da pessoa.
 *
 * Mora ao lado da do telefone e pela mesma razão: duas telas a usam (a ficha da empresa
 * e a entidade emissora da 10.2) e cada rota é um chunk próprio, então importá-la de uma
 * rota arrastaria a outra tela inteira para dentro do pacote.
 *
 * O `cepBr` do contrato aceita as duas formas (`/^\d{5}-?\d{3}$/`), então o hífen que
 * aparece no campo não muda o que a busca de endereço manda para a API.
 */
export function mascararCep(bruto: string): string {
  return mascararCepBr(bruto)
}

export function CampoDeTexto({
  rotulo,
  erro,
  microcopy,
  ajuda,
  ...resto
}: {
  rotulo: string
  erro?: string | undefined
  microcopy?: string | undefined
  /** Dica do campo (D-203); ver o `Campo`. */
  ajuda?: ReactNode
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const automatico = useId()
  const identificador = resto.id ?? automatico
  const idDoErro = `${identificador}-erro`
  const idDaAjuda = `${identificador}-ajuda`
  const idDaDica = `${identificador}-dica`
  return (
    <div>
      <LinhaDoRotulo rotulo={rotulo} para={identificador} ajuda={ajuda} idDaDica={idDaDica} acessorio={undefined} />
      <textarea
        {...resto}
        id={identificador}
        aria-invalid={erro === undefined ? undefined : true}
        aria-describedby={descritoPor(
          microcopy === undefined ? undefined : idDaAjuda,
          ajuda === undefined ? undefined : idDaDica,
          erro === undefined ? undefined : idDoErro,
        )}
        className={`${CLASSE_DO_CAMPO} ${erro === undefined ? '' : 'err'}`}
      />
      {microcopy !== undefined && (
        <p id={idDaAjuda} className="text-[12px] text-muted mt-1">
          {microcopy}
        </p>
      )}
      {erro !== undefined && (
        <p id={idDoErro} className="text-[12px] text-red-600 mt-1 flex items-center gap-1">
          <Icone nome="circle-alert" className="w-3.5 h-3.5" /> <span>{erro}</span>
        </p>
      )}
    </div>
  )
}

/**
 * O SELETOR do sistema: caixa do protótipo fechada, LISTA do protótipo aberta.
 *
 * ## O problema, e por que ele sobreviveu a uma rodada inteira de refino
 *
 * Todo seletor deste app era `<select>` nativo vestido de `.dv-select-btn`. O estado
 * FECHADO ficava idêntico ao protótipo; a lista ABERTA era a do sistema operacional:
 * fonte do sistema, realce azul do Windows, canto reto. Nenhum CSS alcança essa lista.
 *
 * A escolha estava argumentada e o preço foi medido: a lista própria custaria teclado,
 * leitor de tela e o seletor nativo do celular, que são de graça no `<select>`, e
 * custaria também **97 chamadas de `selectOption` em 24 arquivos de teste**, que só
 * funcionam sobre um `<select>` de verdade.
 *
 * ## O desenho que paga o preço sem comprar a dívida
 *
 * O `<select>` CONTINUA sendo o controle. Fica onde sempre esteve, com o mesmo valor, o
 * mesmo `onChange`, o mesmo nome acessível e a mesma ordem de tabulação. O que muda é
 * que, quando o ponteiro é fino e a tela é larga, ele fica transparente e por cima dele
 * vai uma capa desenhada, com a lista do protótipo.
 *
 * - **mouse**: clica na capa, escolhe na lista do protótipo, e a capa escreve no
 *   `<select>` de verdade;
 * - **teclado**: `Tab` chega ao `<select>`, e ali as setas, as letras, Home/End e Enter
 *   são os do sistema operacional, que ninguém reescreve melhor;
 * - **leitor de tela**: anuncia um `combobox` só, o nativo, com as opções nativas. A
 *   capa é `aria-hidden` e não tabula;
 * - **celular**: `pointer: coarse` desliga a capa e a roda nativa volta, que é o melhor
 *   seletor que existe num telefone;
 * - **Playwright**: `selectOption` continua funcionando, porque o elemento continua lá.
 *   `opacity: 0` não torna um elemento invisível para a régua dele.
 *
 * As opções da lista saem dos PRÓPRIOS `<option>` do `children`, e é isso que permitiu
 * migrar os seletores do app trocando só o nome da tag: nenhuma chamada precisou virar
 * um vetor de opções à mão.
 */
/**
 * O texto de um `<option>`, e só o texto.
 *
 * `String(children)` resolveria a maioria dos casos e escreveria `[object Object]` no
 * dia em que alguém pusesse um `<span>` dentro da opção — que é justamente o dia em que
 * ninguém está olhando. Aqui só string e número viram texto; o resto é atravessado, e o
 * que sobra fica vazio, que é honesto.
 */
function textoDe(no: ReactNode): string {
  if (typeof no === 'string') return no
  if (typeof no === 'number') return String(no)
  if (Array.isArray(no)) return no.map((parte) => textoDe(parte as ReactNode)).join('')
  return ''
}

export function SeletorDoSistema({
  className = 'dv-select-btn dv-select-barra max-md:!h-11',
  classeDaCaixa = '',
  variante = 'campo',
  children,
  ...resto
}: {
  /**
   * A largura (e só ela) vai AQUI, não no `className`.
   *
   * O `className` veste o `<select>` e a capa; o invólucro fica de fora, e é ele que a
   * lista mede (`w-full` de quem a posiciona). Enquanto a largura ia no `className`, o
   * botão encolhia e a lista continuava do tamanho do invólucro — em `/tarefas/semana`
   * o botão media 256px e a lista abria com 1120px, a linha inteira da página.
   *
   * Com a largura na caixa, os quatro medem igual por construção: invólucro, `<select>`,
   * capa e lista. É o mesmo acerto que `rh.tsx` já fazia à mão, embrulhando a `Selecao`
   * num `<div className="w-40">` — agora ele tem nome e vale para todas as chamadas.
   */
  classeDaCaixa?: string
  /**
   * `campo` (padrão) desenha a caixa com valor e chevron. `icone` desenha só um botão de
   * ícone — o "kebab" do protótipo 9.1 — e a lista abre alinhada à direita.
   *
   * A variante existe porque um `<select>` de largura inteira dentro de um card do Kanban
   * é um controle de formulário onde deveria haver um gesto: ocupa a base do card inteira
   * para uma ação que o protótipo resolve com 28px. O que NÃO muda é o que está por baixo:
   * o `<select>` nativo continua ali, com o mesmo `aria-label`, e por isso o teclado, o
   * leitor de tela e o `selectOption` dos testes seguem funcionando.
   */
  variante?: 'campo' | 'icone'
} & React.SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  const decora = useConsultaDeTela('(min-width: 768px) and (pointer: fine)')
  const [aberta, setAberta] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)
  const nativo = useRef<HTMLSelectElement>(null)

  const opcoes: { valor: string; rotulo: string }[] = []
  for (const filho of Children.toArray(children)) {
    if (!isValidElement(filho) || filho.type !== 'option') continue
    const props = filho.props as { value?: string | number; children?: ReactNode }
    opcoes.push({ valor: String(props.value ?? ''), rotulo: textoDe(props.children) })
  }

  const valor = String(resto.value ?? '')
  const escolhida = opcoes.find((o) => o.valor === valor)
  const inerte = resto.disabled === true

  /* A lista mede a CAIXA, que é quem carrega a largura (ver o comentário de `classeDaCaixa`). */
  const medirCaixa = useCallback(() => caixa.current?.getBoundingClientRect() ?? null, [])
  const geometriaDaLista = usePosicaoDoPainel(aberta, medirCaixa, {
    // 15rem é o teto que `.dv-select-list` já declarava; a geometria só o respeita.
    alturaDesejada: ALTURA_DA_LISTA,
    largura: variante === 'icone' ? 224 : 'campo',
    alinhar: variante === 'icone' ? 'fim' : 'inicio',
  })

  useEffect(() => {
    if (!aberta) return undefined
    // Campo E painel: o portal tirou a lista de dentro da caixa (ver `painel-flutuante`).
    const foraDaCaixa = (evento: MouseEvent): void => {
      if (foraDoConjunto(evento.target, caixa.current)) setAberta(false)
    }
    const noEscape = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return
      /*
        O mesmo acordo do calendário: a lista consome o primeiro Escape, para que fechar
        o seletor não feche o diálogo que o contém. Sem isto, escolher e desistir dentro
        de um modal fechava o modal inteiro.
      */
      evento.preventDefault()
      evento.stopPropagation()
      setAberta(false)
      nativo.current?.focus()
    }
    document.addEventListener('mousedown', foraDaCaixa)
    document.addEventListener('keydown', noEscape, true)
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa)
      document.removeEventListener('keydown', noEscape, true)
    }
  }, [aberta])

  /*
    A escrita passa pelo ELEMENTO, e não por um evento sintético montado à mão.

    Escrever no `value` do nó pelo setter do protótipo e disparar um `change` que borbulha
    faz o React ver exatamente o evento que veria se a pessoa tivesse usado o seletor
    nativo: mesmo `onChange`, mesma validação, mesmo `data-teste`. Montar um objeto que
    finge ser um evento funcionaria hoje e quebraria no dia em que alguém lesse mais um
    campo dele.
  */
  const escolher = (novo: string): void => {
    setAberta(false)
    const no = nativo.current
    if (no === null) return
    Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set?.call(no, novo)
    no.dispatchEvent(new Event('change', { bubbles: true }))
  }

  return (
    <div
      ref={caixa}
      className={`${decora ? 'dv-select relative' : 'relative'}${classeDaCaixa === '' ? '' : ` ${classeDaCaixa}`}`}
    >
      <select {...resto} ref={nativo} className={`${className}${decora ? ' dv-select-nativo' : ''}`}>
        {children}
      </select>
      {decora ? (
        <>
          {/*
            O PAPEL declarado, e não um enfeite: a capa é o que o mouse clica e o que abre
            a lista. Dizer `role="button"` é escrever o que ela já faz.

            Para leitor de tela nada muda: ela continua fora da árvore (`aria-hidden`), e
            quem responde é o `<select>` por baixo, com o mesmo nome, o mesmo teclado e o
            mesmo `selectOption`. Ela também não ganha foco, porque não recebe `tabindex`,
            e o `getByRole` do Playwright continua sem enxergá-la.

            Sem o papel, toda varredura que procura clicável por
            `button, a[href], [role="button"]` passa por cima do seletor e conclui que o
            controle não está na tela.
          */}
          <div
            aria-hidden="true"
            role="button"
            data-teste="capa-do-seletor"
            className={`${className} dv-select-capa${variante === 'icone' ? ' dv-select-capa-icone' : ''}${inerte ? ' dv-select-capa-inerte' : ''}`}
            onClick={() => {
              if (!inerte) setAberta((estava) => !estava)
            }}
          >
            {variante === 'icone' ? (
              <Icone nome="ellipsis-vertical" className="h-4 w-4 shrink-0" />
            ) : (
              <>
                <span className="dv-select-value truncate">{escolhida?.rotulo ?? ''}</span>
                <Icone nome="chevron-down" className="h-4 w-4 shrink-0 text-faint" />
              </>
            )}
          </div>
          {aberta ? (
            <PainelFlutuante
              as="ul"
              geometria={geometriaDaLista}
              aria-hidden="true"
              /* No modo ícone o botão tem 28px: medir o botão daria uma lista ilegível.
                 Ela ganha largura própria e ancora à direita, como no protótipo — agora
                 pela geometria, que também a impede de sair da janela. */
              className="dv-select-list z-[80] rounded-[10px] border border-hairline bg-white py-1 text-sm shadow-card"
            >
              {opcoes.map((o) => (
                <li
                  key={o.valor}
                  data-valor={o.valor}
                  aria-selected={o.valor === valor}
                  className={`dv-opt flex cursor-pointer items-center justify-between gap-2 px-3 py-2 hover:bg-navy/5 ${o.valor === valor ? 'text-navy' : 'text-text2'}`}
                  onClick={() => {
                    escolher(o.valor)
                  }}
                >
                  <span className="truncate">{o.rotulo}</span>
                  {o.valor === valor ? <Icone nome="check" className="h-4 w-4 shrink-0" /> : null}
                </li>
              ))}
            </PainelFlutuante>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/**
 * Multi-seleção com chips — o `dv-multi` do protótipo (7.1, gaveta de filtros).
 *
 * Por que este NÃO reaproveita o `SeletorDoSistema` logo acima: lá o truque é manter o
 * `<select>` nativo por baixo de uma capa desenhada, e ele funciona porque o controle
 * nativo tem a MESMA caixa que a capa. O equivalente múltiplo do navegador é uma caixa
 * de lista alta, sem chips e sem estado fechado — pôr uma capa em cima dela não seria
 * vesti-la, seria escondê-la. Então aqui o controle é construído.
 *
 * O que se paga por construir, paga-se em acessibilidade explícita, e não em role
 * inventado: o gatilho é um botão com `aria-expanded`, e cada opção é um BOTÃO DE
 * ESTADO (`aria-pressed`), não uma `<li role="option"`> que ninguém alcança pelo
 * teclado. Enter e espaço são os do botão, Escape fecha e devolve o foco ao gatilho,
 * e `getByRole('button', { pressed: true })` enxerga a seleção sem depender de classe.
 *
 * A lista repete o desenho de `.dv-opt` do seletor de valor único de propósito: marca
 * de conferido à direita, texto navy no escolhido. Duas listas com a mesma função e
 * dois desenhos é o que a rodada visual de 31/08 desfez em outros lugares.
 */
export function SelecaoMultipla({
  rotulo,
  opcoes,
  valores,
  aoMudar,
  vazio = 'Selecionar…',
  teste,
}: {
  rotulo: string
  opcoes: readonly { readonly valor: string; readonly rotulo: string }[]
  valores: readonly string[]
  aoMudar: (valores: readonly string[]) => void
  vazio?: string
  teste?: string
}): React.JSX.Element {
  const identificador = useId()
  const [aberta, setAberta] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)
  const gatilho = useRef<HTMLButtonElement>(null)

  const medirCaixaDaMulti = useCallback(() => caixa.current?.getBoundingClientRect() ?? null, [])
  const geometriaDaMulti = usePosicaoDoPainel(aberta, medirCaixaDaMulti, {
    alturaDesejada: ALTURA_DA_LISTA,
    largura: 'campo',
  })

  useEffect(() => {
    if (!aberta) return undefined
    // Campo E painel: o portal tirou a lista de dentro da caixa (ver `painel-flutuante`).
    const foraDaCaixa = (evento: MouseEvent): void => {
      if (foraDoConjunto(evento.target, caixa.current)) setAberta(false)
    }
    const noEscape = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return
      // Consome o Escape antes do diálogo, como o calendário e o seletor simples.
      evento.preventDefault()
      evento.stopPropagation()
      setAberta(false)
      gatilho.current?.focus()
    }
    document.addEventListener('mousedown', foraDaCaixa)
    document.addEventListener('keydown', noEscape, true)
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa)
      document.removeEventListener('keydown', noEscape, true)
    }
  }, [aberta])

  const alternar = (valor: string): void => {
    aoMudar(valores.includes(valor) ? valores.filter((v) => v !== valor) : [...valores, valor])
  }

  return (
    <div>
      <span className="eyebrow text-muted block mb-1.5" id={`${identificador}-rotulo`}>
        {rotulo}
      </span>
      <div className="relative" ref={caixa}>
        {/*
          A caixa é um `<div>`, e não o `<button>` que era: o protótipo põe um botão de
          remover DENTRO de cada chip (0.3:626-638, `aria-label="Remover …"`), e botão
          dentro de botão não é HTML válido — o navegador desfaz o aninhamento e o clique
          no "×" vira clique no gatilho, que reabre a lista em vez de tirar o chip.

          Então: os chips ficam na caixa, cada um com o seu botão de remover, e o gatilho
          que abre a lista é o pedaço restante da caixa, com o chevron do protótipo à
          direita. O foco continua visível porque `estilo.css` dá anel a todo
          `button:focus-visible`.
        */}
        <div className="flex min-h-[44px] w-full flex-wrap items-center gap-1.5 rounded-[10px] border border-hairline bg-white px-2 py-1 text-sm hover:border-navy/40 md:min-h-9">
          {opcoes
            .filter((o) => valores.includes(o.valor))
            .map((o) => (
              <span
                key={o.valor}
                className="inline-flex items-center gap-1 rounded-full bg-navy/[.06] px-2 py-0.5 text-[12px] text-navy"
              >
                {o.rotulo}
                <button
                  type="button"
                  aria-label={`Remover ${o.rotulo}`}
                  onClick={() => {
                    alternar(o.valor)
                  }}
                  className="text-navy/60 hover:text-red-600"
                >
                  <Icone nome="x" className="h-3 w-3" />
                </button>
              </span>
            ))}
          <button
            ref={gatilho}
            type="button"
            aria-expanded={aberta}
            aria-haspopup="true"
            aria-labelledby={`${identificador}-rotulo`}
            data-teste={teste}
            onClick={() => {
              setAberta((estava) => !estava)
            }}
            className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left"
          >
            {valores.length === 0 ? <span className="text-faint">{vazio}</span> : null}
            <Icone nome="chevron-down" className="ml-auto h-4 w-4 shrink-0 text-faint" />
          </button>
        </div>
        {aberta ? (
          <PainelFlutuante
            geometria={geometriaDaMulti}
            role="group"
            aria-labelledby={`${identificador}-rotulo`}
            className="dv-multi-list z-[80] rounded-[10px] border border-hairline bg-white py-1 text-sm shadow-card"
          >
            {opcoes.map((o) => (
              <button
                key={o.valor}
                type="button"
                aria-pressed={valores.includes(o.valor)}
                onClick={() => {
                  alternar(o.valor)
                }}
                className={`dv-opt flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left hover:bg-navy/5 ${valores.includes(o.valor) ? 'text-navy' : 'text-text2'}`}
              >
                <span className="truncate">{o.rotulo}</span>
                {valores.includes(o.valor) ? <Icone nome="check" className="h-4 w-4 shrink-0" /> : null}
              </button>
            ))}
          </PainelFlutuante>
        ) : null}
      </div>
    </div>
  )
}

export function Selecao({
  rotulo,
  erro,
  microcopy,
  ajuda,
  opcoes,
  acessorio,
  ...resto
}: {
  rotulo: string
  erro?: string | undefined
  microcopy?: string | undefined
  /** Dica do campo (D-203); ver o `Campo`. */
  ajuda?: ReactNode
  /**
   * Conteúdo à direita do rótulo — o mesmo `acessorio` que o `Campo` já tem.
   *
   * Existe para o selo do setor derivado do Cliente (D-32, spec 4.3:28), que a 4.3
   * escrevia como microcopy embaixo do campo: ali ele parecia instrução de
   * preenchimento, e não um fato herdado do cadastro da empresa.
   */
  acessorio?: ReactNode
  opcoes: readonly { readonly valor: string; readonly rotulo: string }[]
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const automatico = useId()
  const identificador = resto.id ?? automatico
  const idDoErro = `${identificador}-erro`
  const idDaAjuda = `${identificador}-ajuda`
  const idDaDica = `${identificador}-dica`
  return (
    <div>
      <LinhaDoRotulo rotulo={rotulo} para={identificador} ajuda={ajuda} idDaDica={idDaDica} acessorio={acessorio} />
      {/*
        Até 31/08 este seletor era um `<select>` nativo vestido de `.dv-select-btn`, e a
        lista ABERTA era a do sistema operacional. A decisão C do fundador reviu esse
        acordo, e a lista do protótipo entrou pelo `SeletorDoSistema` — que preserva o
        `<select>` por baixo, e com ele o teclado, o leitor de tela, o seletor do celular
        e as 97 interações de teste. A razão inteira está no cabeçalho dele.
      */}
      <SeletorDoSistema
        {...resto}
        id={identificador}
        aria-invalid={erro === undefined ? undefined : true}
        aria-describedby={descritoPor(
          microcopy === undefined ? undefined : idDaAjuda,
          ajuda === undefined ? undefined : idDaDica,
          erro === undefined ? undefined : idDoErro,
        )}
        className={`dv-select-btn min-h-[44px] md:min-h-0 ${erro === undefined ? '' : 'err'}`}
      >
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </SeletorDoSistema>
      {microcopy !== undefined && (
        <p id={idDaAjuda} className="text-[12px] text-muted mt-1">
          {microcopy}
        </p>
      )}
      {erro !== undefined && (
        <p id={idDoErro} className="text-[12px] text-red-600 mt-1 flex items-center gap-1">
          <Icone nome="circle-alert" className="w-3.5 h-3.5" /> <span>{erro}</span>
        </p>
      )}
    </div>
  )
}

/**
 * O tamanho que todo botão tem, a menos que quem chama diga outro.
 *
 * Ele nasceu em 22/08, depois que a tela 6.2 foi medida contra o protótipo: os botões
 * saíam com **22px de altura e zero padding**, contra os 36px do protótipo — texto
 * espremido dentro de uma borda. E não era só a 6.2: o defeito estava em toda tela cujo
 * autor não soubesse passar `className="h-9 px-4"` à mão, porque os componentes não
 * carregavam tamanho nenhum. Onze chamadas passavam; dezenas não.
 *
 * Nenhuma camada de teste pegava isso — nem lint, nem tipo, nem integração, nem e2e —
 * porque **nenhuma media geometria**. Um botão de 22px tem o mesmo texto, o mesmo papel
 * de acessibilidade e o mesmo clique que um de 36px; ele só é feio, e feio não falha.
 *
 * O `className` de quem chama vem DEPOIS na string, então continua podendo trocar: o
 * botão de largura total do login e o alvo de 44px do celular seguem valendo.
 */
const TAMANHO_PADRAO = 'h-9 px-4'

/**
 * A FORMA do botão — o que Primário, Secundário e o de ícone têm em comum.
 *
 * Ela estava escrita duas vezes aqui dentro e mais **quarenta e uma** à mão nas barras de
 * ação dos cabeçalhos, com raio 8 onde o desenho pede 10 e `px-3 py-1.5` onde ele pede
 * `h-9` (0.3-componentes.html:123-127). Agora existe uma vez.
 *
 * O `display` fica FORA dela de propósito. O `<button>` continua `flex` — era assim, e
 * `flex` estica o botão quando ele não é item de flex, coisa de que o login depende. O
 * `<a>`/`<Link>` entra como `inline-flex`, porque âncora é inline e um `flex` ali viraria
 * uma faixa da largura da página.
 */
const FORMA = `${TAMANHO_PADRAO} items-center justify-center gap-2 rounded-[10px] text-sm`

/**
 * A pele de cada papel do botão secundário, transcrita do protótipo (0.3:124, 3.6:147-148,
 * 4.2:96).
 *
 * `perigo` e `ganho` nasceram porque as fichas os escreviam à mão e cada uma de um jeito:
 * o "Cancelar" da 4.2 era `border-red-300 bg-red-50 text-red-800` no app e o desenho diz
 * `border-red-200 text-red-600` sobre branco; o "Marcar como ganho" da 3.6 era
 * `border-emerald-300 bg-emerald-50 text-emerald-800` e o desenho diz `border-emerald-200
 * text-emerald-700`, também sobre branco. Duas tintas para o mesmo papel é o defeito — a
 * escolha entre elas é o desenho quem faz.
 */
const PELE_DO_TOM = {
  neutro: 'bg-white border border-hairline text-text2 hover:border-navy/40',
  perigo: 'bg-white border border-red-200 text-red-600 hover:bg-red-50',
  ganho: 'bg-white border border-emerald-200 font-medium text-emerald-700 hover:bg-emerald-50',
}

/**
 * As MESMAS classes, para quem não é `<button>`.
 *
 * Metade das ações do cabeçalho é `<Link>` — Novo pedido, Nova empresa, Editar, Emitir
 * NF, Agenda semanal. Elas navegam, e trocá-las por `<button>` custaria o clique do meio,
 * o "abrir em nova aba" e o endereço na barra de status. Como `<Link>` não pode ser
 * `BotaoPrimario`, ele recebe a string que o `BotaoPrimario` usa: uma fonte, dois
 * consumidores.
 *
 * "Voltar" NÃO está na lista, e a ausência é a mesma decisão desta leva: a volta saiu da
 * fileira de ações e virou `componentes/caminho-de-volta.tsx`, acima do título. Citá-la
 * aqui mandaria o próximo leitor procurar um consumidor que não existe mais.
 */
export const CLASSES_DE_LINK_PRIMARIO = `btn-navy inline-flex ${FORMA} font-medium`
export const CLASSES_DE_LINK_SECUNDARIO = `inline-flex ${FORMA} ${PELE_DO_TOM.neutro} transition-colors`

/**
 * A FORMA do botão quadrado de ícone — 36×36, o alvo do protótipo (3.4:340-343).
 *
 * Ela mora aqui em cima, e não dentro do `BotaoDeIcone`, porque tem DOIS consumidores: o
 * componente e a âncora abaixo. Escrita duas vezes, bastaria alguém mexer no raio de um
 * lado para o `tel:` deixar de ser o mesmo alvo que o "Excluir" ao lado dele.
 */
const FORMA_DE_ICONE = 'grid h-9 w-9 shrink-0 place-items-center rounded-[10px] transition-colors'

/** A mesma coisa para a âncora quadrada de ícone (o `tel:` e o `mailto:` da ficha 3.4). */
export const CLASSES_DE_ICONE = `${FORMA_DE_ICONE} ${PELE_DO_TOM.neutro}`

/**
 * Botão primário navy.
 *
 * `carregando` desabilita de verdade (`disabled`), e não só visualmente: a tela 1.1
 * exige uma requisição por submissão, e um botão que só *parece* travado deixa o
 * duplo clique passar.
 */
export function BotaoPrimario({
  carregando = false,
  rotuloDeCarregamento = 'Salvando…',
  children,
  className = '',
  ...resto
}: {
  carregando?: boolean
  rotuloDeCarregamento?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...resto}
      disabled={carregando || resto.disabled === true}
      aria-disabled={carregando ? true : undefined}
      className={`btn-navy flex ${FORMA} font-medium ${carregando ? 'is-loading' : ''} ${className}`}
    >
      {carregando ? (
        <>
          <Icone nome="loader-2" className="w-4 h-4 animate-spin" /> {rotuloDeCarregamento}
        </>
      ) : (
        children
      )}
    </button>
  )
}

export function BotaoSecundario({
  tom = 'neutro',
  children,
  className = '',
  ...resto
}: {
  tom?: 'neutro' | 'perigo' | 'ganho'
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...resto}
      className={`flex ${FORMA} ${PELE_DO_TOM[tom]} transition-colors ${className}`}
    >
      {children}
    </button>
  )
}

/**
 * Botão QUADRADO de ícone — 36×36, o alvo que o protótipo dá a Ligar, E-mail, Editar e
 * Excluir no cabeçalho da ficha (3.4:340-343 e 3.2:145).
 *
 * Ele existe por causa do nome. Um botão só com ícone não tem texto, e as cinco cópias
 * escritas à mão na 3.4 traziam `aria-label` e `title` por disciplina de quem as escreveu
 * — disciplina não é garantia. Aqui o `rotulo` é OBRIGATÓRIO e vira os dois de uma vez:
 * um botão mudo deixa de ser possível de escrever.
 *
 * Só `<button>`. A âncora quadrada (o `tel:` e o `mailto:`) usa `CLASSES_DE_ICONE`, porque
 * um componente que decide sozinho entre `<a>` e `<button>` precisaria de dois sacos de
 * atributos incompatíveis e de um molde para trocá-los.
 */
export function BotaoDeIcone({
  nome,
  rotulo,
  tom = 'neutro',
  className = '',
  ...resto
}: {
  nome: string
  rotulo: string
  tom?: 'neutro' | 'perigo'
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const pele = tom === 'perigo'
    ? 'bg-white border border-hairline text-red-600 hover:border-red-300'
    : PELE_DO_TOM.neutro
  return (
    <button
      type="button"
      {...resto}
      aria-label={rotulo}
      title={rotulo}
      className={`${FORMA_DE_ICONE} disabled:opacity-50 ${pele} ${className}`}
    >
      <Icone nome={nome} className="h-4 w-4" />
    </button>
  )
}

/** Banner de erro do formulário — vermelho, no topo, com a mensagem da API. */
/**
 * A barra de ação no rodapé do formulário — PÓS-V1-003.
 *
 * ## O defeito, que era de geometria e não de estilo
 *
 * Os três formulários longos (empresa, negócio, pedido) tinham a MESMA marcação, copiada:
 * `fixed inset-x-0 bottom-0` com um filho `mx-auto max-w-5xl justify-end`. A barra ocupava
 * a janela inteira e centralizava uma caixa de 1024px **na janela** — enquanto o conteúdo
 * vive numa coluna deslocada pela barra lateral (`md:ml-64`, ou `md:ml-19` no rail) e com
 * o padding do `<main>`.
 *
 * Numa janela de 1920px com a barra expandida, a margem direita do formulário fica em
 * ~1888px e a dos botões em ~1456px: **mais de 400px de recuo**, e ele CRESCE com a
 * largura da tela. Era o "recuados em relação à margem direita" do relato.
 *
 * ## Por que `sticky`, e não `fixed` com deslocamento
 *
 * Para acertar por `fixed` seria preciso ensinar a barra a largura da barra lateral — e o
 * estado do rail é local do `shell`, não há contexto. Qualquer número copiado aqui
 * envelheceria no dia em que o shell mudasse.
 *
 * `sticky` não precisa saber de nada: a barra é filha da coluna de conteúdo, então herda a
 * posição e a largura dela **por construção**. Os negativos de margem sangram o fundo até
 * as bordas da área de conteúdo e o padding devolve os botões à margem do formulário, que
 * é onde o pedido os quer. De quebra, a barra deixa de cobrir o fim do formulário: ela
 * vem DEPOIS do conteúdo, não por cima dele.
 *
 * `z-10` continua acima da capa do campo de data e abaixo do calendário (que hoje é
 * portado com `z-[80]`); e o `ref` do `useRodapeFixo` continua medindo a altura para a
 * pilha de toasts não deitar sobre o botão.
 */
export function RodapeDeFormulario({
  children,
  teste,
}: {
  children: ReactNode
  teste?: string
}): React.JSX.Element {
  const rodape = useRodapeFixo()
  return (
    <div
      ref={rodape}
      {...(teste === undefined ? {} : { 'data-teste': teste })}
      className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-hairline bg-white/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8"
    >
      <div className="flex flex-wrap justify-end gap-2">{children}</div>
    </div>
  )
}

/**
 * `info` (D-203): nota informativa permanente, no papel e na borda do sistema, sem família de
 * cor nova. `ok` é sucesso (verde, com o check), `aviso` pede atenção e `erro` impede. Nota
 * comum não usa `ok`: o verde dizia "deu certo" onde nada tinha acontecido.
 */
export function Banner({ tom = 'erro', children }: { tom?: 'erro' | 'aviso' | 'ok' | 'info'; children: ReactNode }) {
  const estilo = {
    erro: 'bg-red-50 border-red-200 text-red-700',
    aviso: 'bg-amber-50 border-amber-200 text-amber-800',
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    info: 'bg-paper2 border-hairline text-text2',
  }[tom]
  const icone = { erro: 'circle-alert', aviso: 'circle-alert', ok: 'check', info: 'info' }[tom]
  return (
    <div
      role={tom === 'erro' ? 'alert' : undefined}
      className={`mb-4 rounded-[10px] border text-[13px] px-3.5 py-2.5 flex items-start gap-2 ${estilo}`}
    >
      <Icone nome={icone} className="w-4 h-4 mt-0.5 shrink-0" />
      {/*
        `<div>`, e não `<span>`: o resumo de erros do formulário de pedido (spec 4.3
        §Validação) põe uma LISTA aqui dentro, e `<ul>` dentro de `<span>` é aninhamento
        que nenhum validador aceita. Como todo item de flex é blocado, a troca não move
        um pixel de nenhuma das telas que já usam o banner.
      */}
      <div>{children}</div>
    </div>
  )
}
